import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import cn from "classnames";
import { type FC, useMemo, useState } from "react";
import { ButtonIcon } from "../components/ButtonIcon";
import {
	getMonthGridDays,
	getMonthRange,
	getWeekdays,
	isDateInRange,
} from "./calendar.utils";
import { monthYear } from "./date.format";

interface DateSelectorProps {
	initial: LocalDate;
	onChange?: (date: LocalDate) => unknown;
	value?: LocalDate;
}

const dayNumber = DateTimeFormatter.ofPattern("d");

export const DateSelector: FC<DateSelectorProps> = (props) => {
	const { value, onChange } = props;
	const [monthDate, setMonthDate] = useState<LocalDate>(value ?? props.initial);
	const days = useMemo(() => getMonthGridDays(monthDate), [monthDate]);
	const firstDayOfMonth = monthDate.withDayOfMonth(1);
	const now = LocalDate.now();
	const monthRange = getMonthRange(monthDate);
	const weekdays = useMemo(() => getWeekdays(), []);

	const onMonthChange = (value: number) =>
		setMonthDate((x) => x.plusMonths(value));

	return (
		// biome-ignore lint/a11y/useSemanticElements: calendar grid is not a form control group
		<div role="group" aria-label="Calendar date picker">
			<div className="date-range-header">
				<ButtonIcon
					icon="chevron_left"
					aria-label="Previous month"
					onClick={() => onMonthChange(-1)}
				/>
				<div className="date-range-month">{monthDate.format(monthYear)}</div>
				<ButtonIcon
					icon="chevron_right"
					aria-label="Next month"
					onClick={() => onMonthChange(1)}
				/>
			</div>
			<div className="date-range-calendar">
				{weekdays.map((weekday) => {
					const name = weekday.name();
					return <span key={`weekday-${name}`}>{name.substring(0, 1)}</span>;
				})}
				{days.map((x) => {
					if (!isDateInRange(x, monthRange)) {
						return <span key={x.toString()} />;
					}
					const disabled = x.isBefore(now) || x.isBefore(firstDayOfMonth);

					return (
						<button
							className={cn("date", { active: value?.isEqual(x) })}
							disabled={disabled}
							key={x.toString()}
							type="button"
							aria-label={x.toString()}
							aria-pressed={value?.isEqual(x) ?? false}
							onClick={() => onChange?.(x)}
						>
							<span className="circle">{x.format(dayNumber)}</span>
						</button>
					);
				})}
			</div>
		</div>
	);
};
